import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Calendar, Clock, User, Loader2, CheckCircle, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const DAYS_FULL_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const TaskSchedulerDialog = ({ 
  open, 
  onOpenChange, 
  task, 
  memberId, 
  memberName,
  onScheduled,
  theme 
}) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [suggestedSlot, setSuggestedSlot] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(new Date());

  const durationHours = task?.estimated_hours || 1;

  // Get week days starting from Monday
  const getWeekDays = useCallback((startDate) => {
    const start = new Date(startDate);
    const dayOfWeek = start.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to Monday
    start.setDate(start.getDate() + diff);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  }, []);

  const [weekDays, setWeekDays] = useState(getWeekDays(new Date()));

  useEffect(() => {
    setWeekDays(getWeekDays(currentWeek));
  }, [currentWeek, getWeekDays]);

  // Fetch suggested slot when dialog opens
  useEffect(() => {
    if (open && memberId && durationHours > 0) {
      fetchSuggestedSlot();
    }
  }, [open, memberId, durationHours]);

  const fetchSuggestedSlot = async () => {
    if (!memberId) return;
    
    setLoadingSuggestion(true);
    try {
      const res = await api.get(`/calendar/suggest-slot/${memberId}`, {
        params: { duration_hours: durationHours }
      });
      
      if (res.data?.suggested_date) {
        setSuggestedSlot(res.data);
        // Auto-select the suggested slot
        setSelectedDate(res.data.suggested_date);
        setSelectedTime(res.data.suggested_time);
        // Navigate to the suggested week
        setCurrentWeek(new Date(res.data.suggested_date));
      }
    } catch (error) {
      console.error('Error fetching suggested slot:', error);
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const fetchAvailability = async (date) => {
    if (!memberId || !date) return;
    
    setLoadingSlots(true);
    try {
      const res = await api.get(`/calendar/availability/${memberId}`, {
        params: { date }
      });
      setAvailableSlots(res.data.available_slots || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateClick = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setSelectedTime('');
    fetchAvailability(dateStr);
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  const handleUseSuggested = () => {
    if (suggestedSlot) {
      setSelectedDate(suggestedSlot.suggested_date);
      setSelectedTime(suggestedSlot.suggested_time);
      setCurrentWeek(new Date(suggestedSlot.suggested_date));
    }
  };

  const handleSave = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Veuillez sélectionner une date et un créneau');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/tasks/${task.id}`, {
        scheduled_date: selectedDate,
        scheduled_time: selectedTime
      });
      
      toast.success('Tâche planifiée sur le calendrier');
      onScheduled?.({ date: selectedDate, time: selectedTime });
      onOpenChange(false);
    } catch (error) {
      console.error('Error scheduling task:', error);
      toast.error('Erreur lors de la planification');
    } finally {
      setSaving(false);
    }
  };

  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isWeekend = (date) => {
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const isSelected = (date) => {
    return selectedDate === date.toISOString().split('T')[0];
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatWeekRange = () => {
    if (weekDays.length === 0) return '';
    const start = weekDays[0];
    const end = weekDays[6];
    const monthStart = start.toLocaleDateString('fr-FR', { month: 'short' });
    const monthEnd = end.toLocaleDateString('fr-FR', { month: 'short' });
    
    if (monthStart === monthEnd) {
      return `${start.getDate()} - ${end.getDate()} ${monthStart} ${start.getFullYear()}`;
    }
    return `${start.getDate()} ${monthStart} - ${end.getDate()} ${monthEnd} ${start.getFullYear()}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
            Planifier la tâche sur le calendrier
          </DialogTitle>
          <DialogDescription>
            Choisissez une date et un créneau pour bloquer le temps de {memberName || 'ce membre'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Task Info */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-800">{task?.name}</p>
                <p className="text-sm text-slate-500">Durée estimée : {durationHours}h</p>
              </div>
              <Badge variant="outline" className="text-xs">
                <User className="w-3 h-3 mr-1" />
                {memberName || 'Non assigné'}
              </Badge>
            </div>
          </div>

          {/* AI Suggestion */}
          {loadingSuggestion ? (
            <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
              <span className="text-sm text-indigo-700">Recherche du meilleur créneau...</span>
            </div>
          ) : suggestedSlot?.suggested_date ? (
            <div 
              className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleUseSuggested}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  <div>
                    <p className="font-medium text-indigo-800">Créneau suggéré</p>
                    <p className="text-sm text-indigo-600">
                      {suggestedSlot.day_name} {new Date(suggestedSlot.suggested_date).toLocaleDateString('fr-FR')} à {suggestedSlot.suggested_time}
                    </p>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                >
                  Utiliser
                </Button>
              </div>
            </div>
          ) : suggestedSlot?.message ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">{suggestedSlot.message}</p>
            </div>
          ) : null}

          {/* Week Navigation */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={handlePrevWeek}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium text-slate-700">{formatWeekRange()}</span>
            <Button variant="ghost" size="sm" onClick={handleNextWeek}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Week Calendar */}
          <div className="grid grid-cols-7 gap-2">
            {/* Day headers */}
            {DAYS_FR.map((day, idx) => (
              <div key={day} className="text-center text-xs font-medium text-slate-500 py-1">
                {day}
              </div>
            ))}
            
            {/* Day cells */}
            {weekDays.map((date, idx) => {
              const isPast = isDateInPast(date);
              const weekend = isWeekend(date);
              const selected = isSelected(date);
              const today = isToday(date);
              
              return (
                <button
                  key={idx}
                  onClick={() => !isPast && !weekend && handleDateClick(date)}
                  disabled={isPast || weekend}
                  className={cn(
                    "p-2 rounded-lg text-center transition-all",
                    "hover:shadow-md",
                    isPast && "opacity-40 cursor-not-allowed",
                    weekend && "bg-slate-100 text-slate-400 cursor-not-allowed",
                    selected && "ring-2 ring-offset-2",
                    selected && !isPast && !weekend && "bg-indigo-600 text-white",
                    !selected && !isPast && !weekend && "bg-white border hover:border-indigo-300",
                    today && !selected && "border-indigo-400 border-2"
                  )}
                  style={selected ? { backgroundColor: theme?.colors?.accent } : {}}
                >
                  <div className="text-lg font-semibold">{date.getDate()}</div>
                  {today && <div className="text-[10px]">Aujourd'hui</div>}
                </button>
              );
            })}
          </div>

          {/* Time Slots */}
          {selectedDate && (
            <div>
              <Label className="mb-2 block">Créneau horaire</Label>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Chargement des disponibilités...</span>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    Aucun créneau disponible ce jour. Essayez une autre date.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-6 gap-2">
                  {availableSlots.map((slot, idx) => (
                    <Button
                      key={idx}
                      variant={selectedTime === slot.start ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedTime(slot.start)}
                      className={cn(
                        "text-xs",
                        selectedTime === slot.start && "ring-2 ring-offset-1"
                      )}
                      style={selectedTime === slot.start ? { backgroundColor: theme?.colors?.accent } : {}}
                    >
                      {slot.start}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected Summary */}
          {selectedDate && selectedTime && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <div>
                  <p className="font-medium">Créneau sélectionné</p>
                  <p className="text-sm">
                    {DAYS_FULL_FR[new Date(selectedDate).getDay()]} {new Date(selectedDate).toLocaleDateString('fr-FR')} à {selectedTime}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!selectedDate || !selectedTime || saving}
            style={{ backgroundColor: theme?.colors?.accent }}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Enregistrement...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Planifier
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskSchedulerDialog;
